import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Home, MapPin, RefreshCw } from "lucide-react";
import MapCanvas from "@/components/MapCanvas";
import { api } from "@/lib/api";
import { catColor, CategoryIcon, pinHtml } from "@/lib/categories";
import { deriveStatus, isTerror, STATE_STYLE } from "@/lib/iot";

const POLL_MS = 10000;
const SEV = { sos: 0, open: 1, closed: 2, expired: 3, none: 4 };

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

function statusPrefix(state, terror) {
  if (state === "sos") return "SOS Meldung vor ";
  if (state === "open") return terror ? "Terrorsperre geöffnet seit " : "Geöffnet seit ";
  if (state === "closed") return terror ? "Terrorsperre geschlossen seit " : "Geschlossen seit ";
  if (state === "expired") return "Meldung abgelaufen";
  return "Keine Meldungen vorhanden";
}

function iotPopupHtml(obj, state) {
  const s = STATE_STYLE[state] || STATE_STYLE.none;
  const color = catColor(obj.category);
  const img = `<a href="${api.imageUrl(obj.permid)}" target="_blank" rel="noopener"><img class="go-popup__img" src="${api.imageUrl(
    obj.permid,
    400
  )}" alt="" onerror="this.parentNode.style.display='none'"/></a>`;
  const hasAge = state === "sos" || state === "open" || state === "closed";
  const inner = hasAge
    ? `${escapeHtml(statusPrefix(state, isTerror(obj.category)))}<span class="go-popup-age"></span>`
    : escapeHtml(statusPrefix(state, isTerror(obj.category)));
  const status = `<div class="go-popup__status" style="color:${s.color}"><span class="go-popup__statusdot" style="background:${s.color}"></span>${inner}</div>`;
  const live = obj.livePosition
    ? `<div class="go-popup__live" data-testid="iot-popup-live">📍 Live-Standort aus Meldung</div>`
    : "";
  return `<div class="go-popup" data-testid="iot-popup">${img}<div class="go-popup__body"><div class="go-popup__title">${escapeHtml(
    obj.title || "Ohne Titel"
  )}</div><div class="go-popup__cat"><span class="go-popup__dot" style="background:${color}"></span>${escapeHtml(
    obj.category || "Ohne Kategorie"
  )}</div>${status}${live}</div></div>`;
}

function useIsDesktop() {
  const query = "(min-width: 1024px)";
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const handler = (e) => setIsDesktop(e.matches);
    m.addEventListener("change", handler);
    return () => m.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function IotView() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [panTo, setPanTo] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [tick, setTick] = useState(0);
  const clockOffset = useRef(0);
  const cardRefs = useRef({});

  const fetchIot = useCallback(async () => {
    try {
      const { data } = await api.getIot();
      if (data.serverTime) {
        const serverEpoch = new Date(data.serverTime).getTime() / 1000;
        clockOffset.current = serverEpoch - Date.now() / 1000;
      }
      setObjects(data.items || []);
      setLastUpdate(Date.now());
    } catch (e) {
      // keep previous state on failure
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIot();
    const id = setInterval(fetchIot, POLL_MS);
    return () => clearInterval(id);
  }, [fetchIot]);

  // Live 1s tick to recompute relative times + SOS expiry.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    document.title = "Meldezentrale · Weinwoche Wiesbaden 2026";
  }, []);

  const nowSec = Date.now() / 1000 + clockOffset.current;

  const located = useMemo(
    () => objects.filter((o) => o.latitude != null && o.longitude != null),
    [objects]
  );

  const computed = useMemo(
    () =>
      objects.map((o) => {
        const st = deriveStatus(o, nowSec);
        return { ...o, ...st };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objects, tick]
  );

  const byId = useMemo(() => {
    const m = new Map();
    computed.forEach((c) => m.set(c.permid, c));
    return m;
  }, [computed]);

  const stateByPermid = useMemo(() => {
    const m = new Map();
    computed.forEach((c) => m.set(c.permid, c.state));
    return m;
  }, [computed]);

  const stateSig = useMemo(
    () => computed.map((c) => `${c.permid}:${c.state}`).join("|"),
    [computed]
  );

  const mapItems = useMemo(
    () =>
      located.map((o) => ({
        permid: o.permid,
        title: o.title,
        category: o.category,
        latitude: o.latitude,
        longitude: o.longitude,
      })),
    [located]
  );

  const counts = useMemo(() => {
    const c = { sos: 0, open: 0, closed: 0, expired: 0, none: 0 };
    computed.forEach((o) => (c[o.state] = (c[o.state] || 0) + 1));
    return c;
  }, [computed]);

  const sortedList = useMemo(
    () =>
      [...computed].sort(
        (a, b) =>
          (SEV[a.state] ?? 9) - (SEV[b.state] ?? 9) ||
          (a.title || "").localeCompare(b.title || "", "de")
      ),
    [computed]
  );

  const pinHtmlFor = useCallback(
    (it, active) => {
      const state = stateByPermid.get(it.permid) || "none";
      const s = STATE_STYLE[state] || STATE_STYLE.none;
      return pinHtml(it.category, active, { color: s.color, pulse: s.pulse });
    },
    [stateByPermid]
  );

  const selectedState = selectedId ? stateByPermid.get(selectedId) : null;
  const selectedObj = selectedId ? byId.get(selectedId) : null;
  const selectedLive = selectedObj ? !!selectedObj.livePosition : false;

  const popupHtml = useMemo(() => {
    if (!selectedId) return null;
    const obj = objects.find((o) => o.permid === selectedId);
    if (!obj) return null;
    return iotPopupHtml(obj, selectedState || "none");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedState, selectedLive]);

  const popupAge = selectedObj ? selectedObj.ageText || "" : "";

  const select = useCallback((o) => {
    setSelectedId(o.permid);
    if (o.latitude != null) {
      setPanTo({ lat: o.latitude, lng: o.longitude, keepZoom: true, nonce: Date.now() });
    }
    setTimeout(() => {
      const el = cardRefs.current[o.permid];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, 60);
  }, []);

  const legend = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="iot-legend">
      {[
        ["sos", "SOS"],
        ["open", "Geöffnet"],
        ["closed", "Geschlossen"],
        ["expired", "Abgelaufen"],
        ["none", "Keine Meldung"],
      ].map(([k, label]) => (
        <span key={k} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
          <span
            className={`h-2.5 w-2.5 rounded-full ${
              STATE_STYLE[k].pulse ? "animate-pulse" : ""
            }`}
            style={{ background: STATE_STYLE[k].color }}
          />
          {label} <span className="text-slate-400">{counts[k] || 0}</span>
        </span>
      ))}
    </div>
  );

  const StatusRow = ({ o }) => {
    const s = STATE_STYLE[o.state] || STATE_STYLE.none;
    return (
      <button
        key={o.permid}
        ref={(el) => (cardRefs.current[o.permid] = el)}
        onClick={() => select(o)}
        data-testid={`iot-card-${o.permid}`}
        className={`flex w-full items-center gap-3 border-b border-slate-50 p-3 text-left transition-colors ${
          selectedId === o.permid ? "bg-slate-50 ring-1 ring-inset ring-slate-200" : "hover:bg-slate-50"
        }`}
      >
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
          style={{ background: s.color }}
        >
          <CategoryIcon category={o.category} color="#fff" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display font-semibold text-slate-900">
            {o.title || "Ohne Titel"}
          </p>
          <p
            className="mt-0.5 flex items-center gap-1.5 truncate text-xs font-semibold"
            style={{ color: s.color }}
            data-testid={`iot-status-${o.permid}`}
          >
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${s.pulse ? "animate-pulse" : ""}`}
              style={{ background: s.color }}
            />
            {o.statusText}
          </p>
        </div>
        <MapPin size={16} className="shrink-0 text-slate-300" />
      </button>
    );
  };

  return (
    <div className="relative flex h-[100dvh] w-full overflow-hidden bg-slate-100" data-testid="iot-page">
      {/* Desktop sidebar */}
      {isDesktop && (
        <aside
          className="flex w-[26rem] shrink-0 flex-col border-r border-slate-200 bg-white"
          data-testid="iot-sidebar"
        >
          <div className="border-b border-slate-100 px-5 py-4">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-rose-600">
              <Radio size={12} /> Meldezentrale · Live
            </p>
            <h1 className="font-display text-xl font-bold leading-tight text-slate-900">
              Infrastruktur-Status
            </h1>
            <div className="mt-2.5">{legend}</div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="iot-list">
            {loading && (
              <div className="px-4 py-10 text-center text-sm text-slate-400">Lädt…</div>
            )}
            {!loading && sortedList.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-slate-400">
                Keine Infrastruktur-Objekte gefunden.
              </div>
            )}
            {sortedList.map((o) => (
              <StatusRow o={o} key={o.permid} />
            ))}
          </div>
        </aside>
      )}

      {/* Map */}
      <div className="relative h-[100dvh] flex-1 lg:h-auto">
        <MapCanvas
          className="gomap absolute inset-0 z-0"
          items={mapItems}
          selectedId={selectedId}
          onSelect={select}
          panTo={panTo}
          pinHtmlFor={pinHtmlFor}
          renderSig={stateSig}
          popupHtml={popupHtml}
          popupAge={popupAge}
          onBackgroundClick={() => setSelectedId(null)}
        />

        {/* Mobile header */}
        {!isDesktop && (
          <div className="glass pointer-events-auto absolute left-3 right-3 top-3 z-[500] rounded-2xl px-4 py-2.5">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-rose-600">
              <Radio size={12} /> Meldezentrale · Live
            </p>
            <h1 className="font-display text-lg font-bold leading-tight text-slate-900">
              Infrastruktur-Status
            </h1>
            <div className="mt-2">{legend}</div>
          </div>
        )}

        {/* Controls */}
        <div className="absolute right-3 top-3 z-[500] flex flex-col items-end gap-2 lg:flex-row lg:items-center">
          <button
            onClick={fetchIot}
            data-testid="iot-refresh"
            aria-label="Aktualisieren"
            className="glass grid h-11 w-11 place-items-center rounded-2xl text-slate-600 transition-transform hover:scale-[1.05] active:scale-[0.95]"
          >
            <RefreshCw size={17} />
          </button>
          <button
            onClick={() => navigate("/map")}
            data-testid="iot-map-link"
            aria-label="Zur Karte"
            className="glass grid h-11 w-11 place-items-center rounded-2xl text-primary transition-transform hover:scale-[1.05] active:scale-[0.95]"
          >
            <MapPin size={18} />
          </button>
          <button
            onClick={() => navigate("/")}
            data-testid="iot-home-link"
            aria-label="Startseite"
            className="glass grid h-11 w-11 place-items-center rounded-2xl text-slate-600 transition-transform hover:scale-[1.05] active:scale-[0.95]"
          >
            <Home size={18} />
          </button>
        </div>
      </div>

      {/* Mobile bottom list */}
      {!isDesktop && (
        <div
          data-testid="iot-mobile-list"
          className="no-scrollbar absolute inset-x-0 bottom-4 z-[500] max-h-[38vh] overflow-y-auto rounded-t-2xl"
        >
          <div className="mx-3 overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-100">
            {loading && (
              <div className="py-6 text-center text-sm text-slate-400">Lädt…</div>
            )}
            {sortedList.map((o) => (
              <StatusRow o={o} key={o.permid} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
